import { useMemo } from "react";
import { useQueryClient } from "react-query";

import { Box, Column, Text, useToast } from "@hightouch/htui";

import { Card } from "src/components/card";
import {
  type AdInsightsCreativeTagsQuery,
  useAdInsightsCreativeTagsQuery,
  useUpdateCreativeTagMutation,
} from "src/graphql";
import { CreativeTagsEmptyState } from "src/pages/ad-studio/tagging/creative-tags-empty-state";
import { EditableTagList } from "src/pages/ad-studio/tagging/editable-tag-list";
import type { EditableTag } from "src/pages/ad-studio/tagging/tag-values";

import { groupTagsByCategory } from "./creative-tag-categories";
import {
  applyCreativeTagValue,
  type CreativeTagValueChange,
  type CreativeTagView,
  creativeTagReason,
  groupCreativeTags,
  isEditableCreativeTag,
} from "./format-creative-tags";

/** Whose tags to list: the one creative on screen, or every creative of the ad when its creatives are not known. */
export type CreativeTagsScope =
  | { kind: "one"; creativeId: string }
  | { kind: "all" };

type Props = {
  fivetranSyncId: string;
  adId: string;
  creative: CreativeTagsScope;
};

export function CreativeTagsSection({ fivetranSyncId, adId, creative }: Props) {
  const tagsQuery = useAdInsightsCreativeTagsQuery(
    { fivetranSyncId, adId },
    { enabled: Boolean(fivetranSyncId) && Boolean(adId) },
  );

  const allGroups = useMemo(
    () => groupCreativeTags(tagsQuery.data?.adInsightsCreativeTags ?? []),
    [tagsQuery.data],
  );
  const groups =
    creative.kind === "one"
      ? allGroups.filter((group) => group.creativeId === creative.creativeId)
      : allGroups;

  const { toast } = useToast();
  const updateTag = useUpdateCreativeTagValue({ fivetranSyncId, adId });
  const saveTagValue = async (change: CreativeTagValueChange) => {
    try {
      await updateTag.mutateAsync({ fivetranSyncId, ...change });
      toast({
        id: "update-creative-tag",
        title: "Tag updated",
        variant: "success",
      });
    } catch (error) {
      toast({
        id: "update-creative-tag-error",
        title: "Couldn't update tag",
        message: error instanceof Error ? error.message : "Try again.",
        variant: "error",
      });
    }
  };

  return (
    <Card heading="Tags" gap={4}>
      {tagsQuery.isLoading ? (
        <EditableTagList tags={[]} isLoading />
      ) : groups.length === 0 ? (
        <CreativeTagsEmptyState namespace="intelligence" />
      ) : (
        groups.flatMap((group) => {
          const editableDimensionKeys = new Set(
            group.tags
              .filter(isEditableCreativeTag)
              .map((tag) => tag.dimensionKey),
          );
          return groupTagsByCategory(group.tags).map(({ category, tags }) => (
            <Column key={`${group.creativeId}:${category}`}>
              <Box as="h4" py={1}>
                <Text size="sm" fontWeight="semibold" color="text.primary">
                  {category}
                </Text>
              </Box>
              <EditableTagList
                tags={toEditableTags(group.creativeId, tags)}
                isDisabled={(tag) =>
                  updateTag.isLoading ||
                  !editableDimensionKeys.has(tag.dimensionKey)
                }
                onChangeValue={(tag, value) =>
                  saveTagValue({
                    creativeId: group.creativeId,
                    dimensionKey: tag.dimensionKey,
                    value,
                  })
                }
              />
            </Column>
          ));
        })
      )}
    </Card>
  );
}

type TagsCache = AdInsightsCreativeTagsQuery | undefined;

function toEditableTags(
  creativeId: string,
  tags: readonly CreativeTagView[],
): EditableTag[] {
  return tags.map((tag) => ({
    id: `${creativeId}:${tag.dimensionKey}`,
    dimensionKey: tag.dimensionKey,
    name: tag.dimensionName,
    value: tag.values.length <= 1 ? (tag.values[0] ?? null) : tag.values,
    allowedValues: tag.allowedValues,
    valueTooltip: creativeTagReason(tag),
  }));
}

/**
 * The tag mutation with the chip flipped in the cache before the round trip
 * and put back if the save fails. Its own `onSuccess` replaces the global
 * invalidate-everything default from `src/lib/react-query.ts`, so a save
 * refetches the tags and leaves the rest of the drilldown alone.
 */
function useUpdateCreativeTagValue({
  fivetranSyncId,
  adId,
}: {
  fivetranSyncId: string;
  adId: string;
}) {
  const queryClient = useQueryClient();
  const tagsKey = useAdInsightsCreativeTagsQuery.getKey({
    fivetranSyncId,
    adId,
  });

  return useUpdateCreativeTagMutation<Error, { previous: TagsCache }>({
    onMutate: async (change) => {
      await queryClient.cancelQueries(tagsKey);
      const previous = queryClient.getQueryData<TagsCache>(tagsKey);
      queryClient.setQueryData<TagsCache>(tagsKey, (current) =>
        current
          ? {
              ...current,
              adInsightsCreativeTags: applyCreativeTagValue(
                current.adInsightsCreativeTags,
                change,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) {
        queryClient.setQueryData(tagsKey, context.previous);
      }
    },
    onSuccess: () => queryClient.invalidateQueries(tagsKey),
  });
}
